import { ControlledNumberField, ControlledSelect, ControlledStringDateOnlyPicker } from '@if/mui'
import { CardContent, CardHeader, CircularProgress, Grid, Stack } from '@mui/material'
import { CardBorderTop } from '@src/components/Card'
import { CreditApplication } from '@src/data/types'
import { MechanicalWarrantyProduct } from '@src/data/types/MechanicalWarrantyProduct'
import { Control, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { MechanicalWarrantyComputeResultDto } from '../../types/ComputeMechanicalWarrantyResultDto'
import { MechanicalWarrantyWorksheetDto } from '../../types/mechanicalWarrantyWorksheetSchema'
import { useMechanicalWarrantyWorksheetDetails } from '../hooks/useMechanicalWarrantyWorksheetDetails'

interface Props {
  creditApplication: CreditApplication
  computedLoanAmounts: MechanicalWarrantyComputeResultDto | null
  isComputing: boolean
  control: Control<MechanicalWarrantyWorksheetDto>
  mechanicalWarrantyProducts: MechanicalWarrantyProduct[]
  minFirstPaymentDate: Date
  maxFirstPaymentDate: Date
  setValue: UseFormSetValue<MechanicalWarrantyWorksheetDto>
}

const MechanicalWarrantyWorksheetDetails = ({
  creditApplication,
  computedLoanAmounts,
  isComputing,
  control,
  mechanicalWarrantyProducts,
  minFirstPaymentDate,
  maxFirstPaymentDate,
  setValue,
}: Props) => {
  const { t } = useTranslation()

  const { maxActivationDate, paymentFrequency, paymentFrequencyOptions, termOptions } =
    useMechanicalWarrantyWorksheetDetails({
      control,
      setValue,
      mechanicalWarrantyProducts,
      computedLoanAmounts,
      creditApplicationExpiresOn: creditApplication.expiresOn,
      creditApplication,
    })

  return (
    <CardBorderTop>
      <CardHeader title={t('worksheet.financing')} />
      <CardContent component={Grid} spacing={2} container>
        <Grid size={4}>
          {isComputing ? (
            <Grid sx={{ mb: 3 }}>
              <Stack direction="row" justifyContent="flex-start" alignItems="center" sx={{ minHeight: 40 }}>
                <CircularProgress size={24} />
              </Stack>
            </Grid>
          ) : (
            <ControlledNumberField
              control={control}
              name="amountRequested"
              label={t('worksheetCommon.loanAmount')}
              disabled
              fullWidth
              isMoney
            />
          )}
        </Grid>

        <Grid size={4}>
          <ControlledSelect
            label={t('worksheet.paymentFrequency')}
            control={control}
            name="paymentFrequency"
            options={paymentFrequencyOptions}
            disableEmptyOption
            fullWidth
          />
        </Grid>

        <Grid size={4}>
          <ControlledSelect
            label={t('worksheetCommon.term')}
            name="term"
            control={control}
            options={termOptions}
            disableEmptyOption
            fullWidth
            disabled
          />
        </Grid>

        <Grid size={4}>
          <ControlledStringDateOnlyPicker
            name="deliveryOn"
            control={control}
            label={t('worksheetCommon.activationDate')}
            disablePast
            minDate={new Date()}
            maxDate={maxActivationDate}
            fullWidth
          />
        </Grid>

        <Grid size={4}>
          <ControlledStringDateOnlyPicker
            name="firstPaymentOn"
            control={control}
            label={t('worksheet.firstPaymentOn')}
            disablePast
            minDate={minFirstPaymentDate}
            maxDate={maxFirstPaymentDate}
            disabled={!paymentFrequency}
            fullWidth
          />
        </Grid>
      </CardContent>
    </CardBorderTop>
  )
}

export default MechanicalWarrantyWorksheetDetails
